import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState, AppTheme, ProfileReducerState, UserAttendanceInterface, MinistryMembershipReducerState } from "../../../../../interfaces";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Container,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Avatar,
  Paper
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { DialogModal, ErrorView } from "../../../../../components";
import { clearMinistryMembershipError, getMinistryMembership, deleteMinistryMembership } from "../../../../../store/actions";
import { checkUserAccess } from "../../../../../util/permission-checker";
import PopupMenuDialog from "../../../../../components/PopupMenuDialog";
import AddMembershipDetailsModal from "./Modals/AddMembershipDetailsModal";
import userImg from "../../../../../assets/user-img.png";
import { Link, useParams } from "react-router-dom";

interface MembersProps {
  ministryMembership: MinistryMembershipReducerState;
  profile: ProfileReducerState;
  appTheme: AppTheme;
  getMinistryMembership: (ministry_id: string, search: string, page: number, per_page: number) => void;
  clearMinistryMembershipError: () => void;
  deleteMinistryMembership: (ministry_id: string, user_id: string) => void;
}

const useStyles = (appTheme: AppTheme) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        width: "100%"
      },
      paper: {
        backgroundColor: appTheme.section.backgroundColor,
        width: "100%",
        padding: 15,
        marginTop: 10,
        marginBottom: 10
      },
      avatar: {
        height: 80,
        width: 80,
        display: "block",
        margin: "auto",
        border: "1px solid black"
      },
      icon: {
        color: theme.palette.text.primary,
        padding: 10,
        borderRadius: 30,
        backgroundColor: appTheme.iconBackgroundColor,
        "&:hover": {
          backgroundColor: appTheme.iconBackgroundHoverColor
        }
      },
      searchButton: {
        marginRight: "-21px",
        height: "55px",
        borderRadius: "30px",
        width: "100px"
      },
      textInput: {
        marginTop: "5px",
        width: "300px"
      },
      textInputControl: {
        borderRadius: "30px",
        backgroundColor: appTheme.section.backgroundColor
      },
      container: {
        minHeight: "60vh"
      },
      textInputIcon: {
        color: "#000"
      }
    })
  );

const Members: React.FC<MembersProps> = (props) => {
  const { appTheme, ministryMembership, profile, getMinistryMembership, deleteMinistryMembership, clearMinistryMembershipError } = props;

  const { ministry_id, ministry_name } = useParams<{ ministry_id: string; ministry_name: string }>();

  const [search, setSearch] = useState<string>(ministryMembership.search);
  const [page, setPage] = useState<number>(ministryMembership.page);
  const [perPage, setPerPage] = useState<number>(ministryMembership.per_page ?? 20);
  const [selectedRecord, setSelectedRecord] = useState<UserAttendanceInterface | null>(null);
  const [popUpDialogOpen, setPopUpDialogOpen] = useState<boolean>(false);

  const popUpMenuItems = useMemo(() => {
    return [{ text: "Delete Membership", permission: "delete-ministry-" + ministry_id + "-membership" }];
  }, [ministry_id]);

  const [menuItems, setMenuItems] = useState<Array<string>>(
    popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text)
  );

  useEffect(() => {
    setMenuItems(popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text));
  }, [popUpMenuItems, profile.permissions]);

  const [addMembershipDetailsModalOpen, setAddMembershipDetailsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    getMinistryMembership(ministry_id, search, page, perPage);
  }, [ministry_id, search, page, perPage, getMinistryMembership]);

  const styles = useStyles(appTheme)();

  type DialogOptions = {
    open: boolean;
    body: string;
  };

  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({ open: false, body: "" });

  const dismissDialogModal = useCallback(() => {
    setDialogOptions({ open: false, body: "" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      clearMinistryMembershipError();
    };
  }, [clearMinistryMembershipError]);

  useEffect(() => {
    if (!ministryMembership.error && ministryMembership.response && ministryMembership.response.data.status !== "Ok") {
      clearMinistryMembershipError();

      setSelectedRecord(null);

      setAddMembershipDetailsModalOpen(false);

      setDialogOptions({ open: true, body: ministryMembership.response.data.status });

      getMinistryMembership(ministry_id, "", 0, perPage);
    }
  }, [ministryMembership.error, ministryMembership.response, ministry_id, perPage, getMinistryMembership, clearMinistryMembershipError]);

  useEffect(() => {
    if (addMembershipDetailsModalOpen) {
      return;
    }

    const requestData = ministryMembership.error ? ministryMembership : null;

    if (!requestData) {
      return;
    }

    if (requestData.error && requestData.error.data && requestData.error.status) {
      let body: string = requestData.error.data.status;

      if (requestData.error.status === 500) {
        body = requestData.error.data.content;
      }

      setDialogOptions({ open: true, body: body });
    } else {
      if (requestData.error && requestData.error.message) {
        setDialogOptions({ open: true, body: requestData.error.message });
      }
    }
  }, [addMembershipDetailsModalOpen, ministryMembership]);

  const onMenuItemSelected = (menuItem: string) => {
    setPopUpDialogOpen(false);

    if (!selectedRecord) {
      return;
    }

    switch (menuItem) {
      case "Delete Membership":
        if (window.confirm("Would you like to delete this membership ?")) {
          deleteMinistryMembership(ministry_id, selectedRecord.user_id);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.root}>
      <DialogModal open={dialogOptions.open} body={dialogOptions.body} onDismissed={dismissDialogModal} />
      <AddMembershipDetailsModal open={addMembershipDetailsModalOpen} onDismissed={() => setAddMembershipDetailsModalOpen(false)} />
      <PopupMenuDialog
        open={popUpDialogOpen}
        onDismissed={() => setPopUpDialogOpen(false)}
        onMenuItemSelected={onMenuItemSelected}
        menuItems={menuItems}
      />
      <Container maxWidth="lg">
        <Paper className={styles.paper}>
          {!addMembershipDetailsModalOpen && ministryMembership.error ? (
            <ErrorView error={ministryMembership.error} onRetry={() => getMinistryMembership(ministry_id, search, page, perPage)} />
          ) : (
            <React.Fragment>
              <Grid container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      {!addMembershipDetailsModalOpen && ministryMembership.loading && (
                        <CircularProgress style={{ display: "block", margin: "auto" }} color="primary" />
                      )}
                    </Grid>
                    <Grid item>
                      <Typography variant="h3" color="primary">
                        Members
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container spacing={2}>
                    <Grid item>
                      <TextField
                        onChange={(event) => {
                          setSearch(event.currentTarget.value);
                        }}
                        error={false}
                        value={search}
                        variant="outlined"
                        placeholder="Search ..."
                        className={styles.textInput}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search className={styles.textInputIcon} />
                            </InputAdornment>
                          ),
                          classes: { root: styles.textInputControl }
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        style={{ marginTop: 10 }}
                        component={Link}
                        to={`/dashboard/ministries/${ministry_id}/${ministry_name}`}
                        variant="outlined"
                        size="large">
                        Go Back
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <br />
              <TableContainer className={styles.container}>
                <Table stickyHeader aria-label="ministry-membership">
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "Profile Image", minWidth: 50 },
                        { label: "ID", minWidth: 100 },
                        { label: "Name", minWidth: 150 },
                        { label: "Type", minWidth: 100 },
                        { label: "Username", minWidth: 80 },
                        { label: "Email", minWidth: 100 },
                        { label: "Mobile No", minWidth: 100 },
                        { label: "Residence", minWidth: 150 },
                        { label: "Options", minWidth: 100 }
                      ].map((column) => (
                        <TableCell key={column.label} align="center" style={{ minWidth: column.minWidth }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ministryMembership.data.map((userAttendance: UserAttendanceInterface) => {
                      const _id = userAttendance.user_id;
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={_id}>
                          {["img", "user_id", "name", "user_type", "username", "email", "mobile_no", "residence", "options"].map((column) => {
                            const key = `${_id}-${column}`;

                            const data = (userAttendance as unknown as { [key: string]: any })[column];
                            if (column === "options") {
                              return (
                                <TableCell key={key} align="center">
                                  <Button
                                    disabled={menuItems.length === 0}
                                    variant="outlined"
                                    onClick={() => {
                                      setSelectedRecord(userAttendance);
                                      setPopUpDialogOpen(true);
                                    }}>
                                    Options
                                  </Button>
                                </TableCell>
                              );
                            } else if (column === "img") {
                              return (
                                <TableCell key={key} align="center">
                                  <Avatar className={styles.avatar} alt={userAttendance.name} src={data ? data : userImg} />
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell key={key} align="center">
                                  {data}
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justifyContent="space-between" style={{ margin: "0px 10px 0px 10px" }}>
                <Grid item>
                  <Button
                    disabled={!checkUserAccess("add-ministry-" + ministry_id + "-membership", profile.permissions)}
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setSelectedRecord(null);
                      setAddMembershipDetailsModalOpen(true);
                    }}>
                    Add New Member
                  </Button>
                </Grid>
                <Grid item>
                  <TablePagination
                    component="div"
                    count={ministryMembership.total}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[20, 50, 100, 1000]}
                    onRowsPerPageChange={(event) => {
                      setPerPage(parseInt(event.target.value));
                      setPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </React.Fragment>
          )}
        </Paper>
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, void, AnyAction>) => {
  return {
    getMinistryMembership: (ministry_id: string, search: string, page: number, per_page: number) =>
      dispatch(getMinistryMembership(ministry_id, search, page, per_page)),
    clearMinistryMembershipError: () => dispatch(clearMinistryMembershipError()),
    deleteMinistryMembership: (ministry_id: string, user_id: string) => dispatch(deleteMinistryMembership(ministry_id, user_id))
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    ministryMembership: state.ministryMembership,
    profile: state.profile,
    appTheme: state.general.theme
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Members);
